import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static values = { districtId: Number, signedIn: Boolean, currentUserId: Number }
  static targets = [ "panel", "toggle", "messages", "onlineCount", "badge", "header" ]

  connect() {
    this.subscription = null
    this.unreadCount = 0
    this.heartbeatInterval = null
    this.#observeMessages()
  }

  disconnect() {
    this.unsubscribe()
    this.#stopHeartbeat()
    if (this.observer) this.observer.disconnect()
  }

  toggle() {
    const isHidden = this.panelTarget.classList.contains("hidden")

    if (isHidden) {
      this.panelTarget.classList.remove("hidden")
      this.toggleTarget.classList.add("hidden")
      this.resetBadge()
      this.subscribe()
      this.scrollToBottom()
    } else {
      this.panelTarget.classList.add("hidden")
      this.toggleTarget.classList.remove("hidden")
      this.unsubscribe()
    }
  }

  close() {
    this.panelTarget.classList.add("hidden")
    this.toggleTarget.classList.remove("hidden")
    this.unsubscribe()
  }

  subscribe() {
    if (this.subscription || !this.signedInValue) return

    this.subscription = consumer.subscriptions.create(
      { channel: "ChatChannel", district_id: this.districtIdValue },
      {
        connected: () => this.#startHeartbeat(),
        disconnected: () => this.#stopHeartbeat(),
        received: (data) => {
          if (data.count !== undefined) {
            this.onlineCountTarget.textContent = data.html
          }
        }
      }
    )
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
    this.#stopHeartbeat()
  }

  resetBadge() {
    this.unreadCount = 0
    this.#renderBadge()
  }

  incrementBadge() {
    this.unreadCount += 1
    this.#renderBadge()
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  handleScroll() {
    if (!this.hasHeaderTarget) return
    if (this.messagesTarget.scrollTop > 0) {
      this.headerTarget.classList.add("scrolled")
    } else {
      this.headerTarget.classList.remove("scrolled")
    }
  }

  #renderBadge() {
    if (!this.hasBadgeTarget) return

    if (this.unreadCount > 0) {
      this.badgeTarget.textContent = this.unreadCount > 99 ? "99+" : this.unreadCount.toString()
      this.badgeTarget.classList.remove("hidden")
    } else {
      this.badgeTarget.classList.add("hidden")
    }
  }

  #observeMessages() {
    this.observer = new MutationObserver((mutations) => {
      const hasNewNodes = mutations.some((m) => Array.from(m.addedNodes).some((n) => n.nodeType === Node.ELEMENT_NODE))
      if (!hasNewNodes) return

      if (this.panelTarget.classList.contains("hidden")) {
        this.incrementBadge()
      } else if (this.#isScrolledNearBottom()) {
        this.scrollToBottom()
      }
    })

    this.observer.observe(this.messagesTarget, { childList: true })
  }

  #isScrolledNearBottom() {
    const threshold = 100
    return this.messagesTarget.scrollHeight - this.messagesTarget.scrollTop - this.messagesTarget.clientHeight < threshold
  }

  #startHeartbeat() {
    if (this.heartbeatInterval) return
    this.heartbeatInterval = setInterval(() => {
      if (this.subscription) this.subscription.perform("heartbeat")
    }, 30000)
  }

  #stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }
}
